import React from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/compact';
import lodashValues from 'lodash/fp/values';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import { getDueDate } from 'common/marketingPartnerListingHelpers';

import { LISTING_OCCUPANT_TYPE_VACANT } from 'common/listingConstants';
import {
  AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_ANY_TIME,
  AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE,
  MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM,
  PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY,
  PROPERTY_SETUP_ACCESS_TYPE_HIDDEN,
  PROPERTY_SETUP_ACCESS_TYPE_OPTION_ITEMS,
  PROPERTY_SETUP_ACCESS_METHOD_OPTION_ITEMS,
  MARKETING_PARTNER_LISTING_SUNROOM_COMMISSION,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE,
  PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX,
  PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX,
} from 'common/marketingPartnerListingConstants';

import {
  makeListingFullAddressString,
  hasListingPetWeightLimit,
  getMarketSecurityDepositLabel,
} from 'common/listingHelpers';

import { formatPrice, formatPercentage, formatPhoneNumber } from 'common/formatters';

import { hasRoleLandlord } from 'common/userRoles';

import pointInsidePolygon from 'common/util/pointInsidePolygon';
import useBranch from 'client/hooks/useBranch';

import SrCard, {
  SR_CARD_BULLET_COLOR_YELLOW,
  SR_CARD_BULLET_COLOR_BLUE,
} from 'client/components/SrCard/SrCard';
import SrBox from 'client/components/SrBox';
import SrInfoTile from 'client/components/SrInfoTile/SrInfoTile';
import SrText from 'client/components/SrText';
import SrSeparator from 'client/components/SrSeparator';

const SrLocalInfoTile = ({ marginTop = true, value, ...props }) => (
  <SrBox marginTop={marginTop ? '21px' : '0px'}>
    <SrInfoTile value={Array.isArray(value) ? lodashCompact(value).join('\n') : value} {...props} />
  </SrBox>
);

const SrLocalSeparator = ({ marginTop = true }) => (
  <SrBox marginTop={marginTop ? '17px' : '0px'}>
    <SrSeparator />
  </SrBox>
);

const SrPropertySummary = ({ formikBag: { values }, onEditStep }) => {
  const { currentUser, marketsById } = useBranch({
    currentUser: ['currentUser'],
    marketsById: ['markets', 'byId'],
  });

  const isLandlord = hasRoleLandlord(currentUser.roles);

  const isSunroom =
    values.listingAgreementSignedBy === MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM &&
    !isLandlord;

  const marketsList = lodashValues(marketsById);
  let showVirtual3dTourOptIn = false;
  let securityDepositLabel = getMarketSecurityDepositLabel(null, true);

  if (values.locationLongitude && values.locationLatitude) {
    for (const market of marketsList) {
      const point = [values.locationLongitude, values.locationLatitude];
      if (pointInsidePolygon(point, market.serviceZone.coordinates)) {
        showVirtual3dTourOptIn = market.virtual3dTourOptInEnabled;
        securityDepositLabel = getMarketSecurityDepositLabel(market, true);
        break;
      }
    }
  }

  return (
    <>
      <SrCard
        color={SR_CARD_BULLET_COLOR_YELLOW}
        header={isLandlord ? 'Order Details' : 'Package Details'}
      >
        {!isLandlord && (
          <SrLocalInfoTile
            marginTop
            label="Handling Lease Listing"
            value={isSunroom ? 'Refer Client to Sunroom' : 'Agent + Sunroom'}
            onEditClick={() => onEditStep('leaseAgreement')}
          />
        )}
        {isSunroom && (
          <SrLocalInfoTile
            marginTop
            label="Commission"
            value={[
              `Client pays ${formatPercentage(values.commissionPercentClientPays)}`,
              `You earn ${formatPercentage(
                values.commissionPercentClientPays - MARKETING_PARTNER_LISTING_SUNROOM_COMMISSION,
              )}`,
              `Sunroom earns ${formatPercentage(MARKETING_PARTNER_LISTING_SUNROOM_COMMISSION)}`,
            ]}
            onEditClick={() => onEditStep('commission')}
          />
        )}
        {isSunroom && (
          <SrLocalInfoTile
            marginTop
            label="What happens next?"
            value="A Sunroom representative will contact your client to get the listing live.
          You'll be notified when the property goes live on market."
            valueColor="green"
          />
        )}
        {!isSunroom && values.availableForSetupDate && (
          <SrLocalInfoTile
            marginTop
            label="Live On Market"
            value={
              <SrText font="greycliff" color="green" size="21" weight="bolder">
                {getDueDate(values.availableForSetupDate).format('dddd, MMM Do')}
              </SrText>
            }
          />
        )}
      </SrCard>
      <SrCard color={SR_CARD_BULLET_COLOR_BLUE} header="Property">
        <SrLocalInfoTile
          label="Property Type"
          value={values.propertyType}
          onEditClick={() => onEditStep('address')}
        />
        <SrLocalInfoTile
          label="Address"
          value={makeListingFullAddressString(values)}
          onEditClick={() => onEditStep('address')}
        />
        {!isSunroom && values.yearBuilt && (
          <SrLocalInfoTile
            label="Year Built"
            value={values.yearBuilt}
            onEditClick={() => onEditStep('size')}
          />
        )}
        {!isSunroom && values.yearRenovated && (
          <SrLocalInfoTile
            label="Year Renovated"
            value={values.yearRenovated}
            onEditClick={() => onEditStep('size')}
          />
        )}
        {!isSunroom && values.bedrooms && (
          <SrLocalInfoTile
            label="Beds"
            value={values.bedrooms}
            onEditClick={() => onEditStep('size')}
          />
        )}
        {!isSunroom && values.bathrooms && (
          <SrLocalInfoTile
            label="Baths"
            value={values.bathrooms}
            onEditClick={() => onEditStep('size')}
          />
        )}
        {!isSunroom && values.squareFeet && (
          <SrLocalInfoTile
            label="Square Feet"
            value={values.squareFeet}
            onEditClick={() => onEditStep('size')}
          />
        )}
        {(values.price || values.securityDeposit) && (
          <SrLocalInfoTile
            label="Price"
            value={
              values.price && values.securityDeposit
                ? [
                    `${formatPrice(values.price)} Rent`,
                    `${formatPrice(values.securityDeposit)} ${securityDepositLabel}`,
                  ]
                : values.price
                ? `${formatPrice(values.price)} Rent`
                : `${formatPrice(values.securityDeposit)} ${securityDepositLabel}`
            }
            onEditClick={() => onEditStep(isSunroom ? 'unitPrice' : 'price')}
          />
        )}
        {isSunroom && (
          <SrLocalInfoTile
            label="Client"
            value={[[values.landlordName, values.landlordPhone].join(', '), values.landlordEmail]}
            onEditClick={() => onEditStep('landlord')}
          />
        )}
        {!isSunroom && (
          <>
            <SrLocalSeparator />
            <SrLocalInfoTile
              label="Occupant Status"
              value={[
                values.occupantType,
                values.occupantType !== LISTING_OCCUPANT_TYPE_VACANT &&
                  [values.occupantName, values.occupantPhone].join(', '),
                values.occupantEmail,
              ]}
              onEditClick={() => onEditStep('occupant')}
            />
            <SrLocalSeparator />
            <SrLocalInfoTile
              label="Earliest Install Date"
              value={momentWithAustinTimezone(values.availableForSetupDate).format('dddd, MMM Do')}
              onEditClick={() => onEditStep('availability')}
            />
            <SrLocalInfoTile
              label="Earliest Move-In Date"
              value={momentWithAustinTimezone(values.availableDate).format('dddd, MMM Do')}
              onEditClick={() => onEditStep('availability')}
            />
            <SrLocalSeparator />
            {values.accessMethod && (
              <SrLocalInfoTile
                label="Access Method"
                value={
                  PROPERTY_SETUP_ACCESS_METHOD_OPTION_ITEMS.find(
                    ({ value }) => value === values.accessMethod,
                  )?.label
                }
                onEditClick={() => onEditStep('access')}
              />
            )}
            {values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE && (
              <SrLocalInfoTile
                label="Key Access"
                value={
                  PROPERTY_SETUP_ACCESS_TYPE_OPTION_ITEMS.find(
                    ({ value }) => value === values.propertySetupAccessType,
                  )?.label
                }
                onEditClick={() => onEditStep('access')}
              />
            )}
            {values.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE &&
              values.propertyAccessCode && (
                <SrLocalInfoTile
                  label="Access Code"
                  value={values.propertyAccessCode}
                  onEditClick={() => onEditStep('access')}
                />
              )}
            {values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE &&
              values.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_HIDDEN &&
              values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE && (
                <SrLocalInfoTile
                  label="Key Pickup Description"
                  value={values.keyPickupDescription}
                  onEditClick={() => onEditStep('access')}
                />
              )}
            {values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE &&
              values.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX &&
              values.supraCbsCode && (
                <SrLocalInfoTile
                  label="Supra CBS Code"
                  value={values.supraCbsCode}
                  onEditClick={() => onEditStep('access')}
                />
              )}
            {values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE &&
              values.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX &&
              values.lockboxCode && (
                <SrLocalInfoTile
                  label="Combo Lockbox Code"
                  value={values.lockboxCode}
                  onEditClick={() => onEditStep('access')}
                />
              )}
            {values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE &&
              values.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY && (
                <SrLocalInfoTile
                  label="Key Contact"
                  value={[
                    [values.propertyAccessContactName, values.propertyAccessContactPhone].join(
                      ', ',
                    ),
                    values.propertyAccessContactEmail,
                  ]}
                  onEditClick={() => onEditStep('access')}
                />
              )}
            {values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE &&
              values.dateKeyExpectedAtListing && (
                <SrLocalInfoTile
                  label="Date Key Is Expected At Listing"
                  value={momentWithAustinTimezone(values.dateKeyExpectedAtListing).format(
                    'MM/DD/YYYY',
                  )}
                  onEditClick={() => onEditStep('access')}
                />
              )}
            {values.gateCode && (
              <SrLocalInfoTile
                label="Gate Code"
                value={values.gateCode}
                onEditClick={() => onEditStep('access')}
              />
            )}
            {values.alarmCode && (
              <SrLocalInfoTile
                label="Alarm Code"
                value={values.alarmCode}
                onEditClick={() => onEditStep('access')}
              />
            )}
            <SrLocalSeparator />
            {!isLandlord && (
              <SrLocalInfoTile
                label="Install Sunlock"
                value={[
                  values.requireSunlock ? 'Yes' : 'No',
                  !values.requireSunlock && values.noRequireSunlockReason,
                ]}
                onEditClick={() => onEditStep('marketingItems')}
              />
            )}
            {!isLandlord &&
              !isSunroom &&
              values.landlordPropertyAccessNotificationAlert &&
              !values.landlordNotificationAlert && (
                <SrLocalInfoTile
                  label="Will Send Access Instructions To:"
                  value={[
                    values.landlordName,
                    values.landlordPhone && formatPhoneNumber(values.landlordPhone),
                    values.landlordEmail,
                  ]}
                  onEditClick={() => onEditStep('marketingItems')}
                />
              )}
            {!isLandlord &&
              !isSunroom &&
              !values.landlordPropertyAccessNotificationAlert &&
              values.landlordNotificationAlert && (
                <SrLocalInfoTile
                  label="Will Send Client Updates To:"
                  value={[
                    values.landlordName,
                    values.landlordPhone && formatPhoneNumber(values.landlordPhone),
                    values.landlordEmail,
                  ]}
                  onEditClick={() => onEditStep('marketingItems')}
                />
              )}
            {!isLandlord &&
              !isSunroom &&
              values.landlordPropertyAccessNotificationAlert &&
              values.landlordNotificationAlert && (
                <SrLocalInfoTile
                  label="Will Send Client Updates And Access Instructions To:"
                  value={[
                    values.landlordName,
                    values.landlordPhone && formatPhoneNumber(values.landlordPhone),
                    values.landlordEmail,
                  ]}
                  onEditClick={() => onEditStep('marketingItems')}
                />
              )}
            <SrLocalInfoTile
              label="Yard Sign"
              value={values.yardSignAllowed ? 'Allowed' : 'Not allowed'}
              onEditClick={() => onEditStep('marketingItems')}
            />
            <SrLocalInfoTile
              label="Photography"
              value={
                values.requirePhotos
                  ? [
                      'Need photos',
                      values.availableForPhotoDate &&
                        // eslint-disable-next-line max-len
                        `Earliest date is ${momentWithAustinTimezone(
                          values.availableForPhotoDate,
                        ).format('MMM Do')}`,
                      values.availableForPhotoDateSchedule ===
                        AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_ANY_TIME && 'Go any time of day',
                      values.availableForPhotoDateSchedule ===
                        AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE && 'Call to schedule',
                    ]
                  : ["Don't need photos", values.linkToExistingPhotos && 'Link to photos given']
              }
              onEditClick={() => onEditStep('marketingItems')}
            />
            {showVirtual3dTourOptIn && (
              <SrLocalInfoTile
                label="3D Tour"
                value={
                  values.requireVirtual3dTour
                    ? [
                        'Need 3D tour',
                        values.availableForPhotoDate &&
                          // eslint-disable-next-line max-len
                          `Earliest date is ${momentWithAustinTimezone(
                            values.availableForPhotoDate,
                          ).format('MMM Do')}`,
                        values.availableForPhotoDateSchedule ===
                          AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_ANY_TIME && 'Go any time of day',
                        values.availableForPhotoDateSchedule ===
                          AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE && // eslint-disable-line max-len
                          'Call to schedule',
                      ]
                    : ["Don't need 3D tour"]
                }
                onEditClick={() => onEditStep('marketingItems')}
              />
            )}
            {values.requirePhotos &&
              values.availableForPhotoDateSchedule ===
                AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE && (
                <SrLocalInfoTile
                  label="Scheduling Contact"
                  value={[
                    values.availableForPhotoContactName,
                    values.availableForPhotoContactPhone,
                  ]}
                  onEditClick={() => onEditStep('marketingItems')}
                />
              )}
            <SrLocalSeparator />
            <SrLocalInfoTile
              label="Pets"
              value={[
                values.catsAllowed ? 'Cats Allowed' : 'Cats Not Allowed',
                values.dogsAllowed ? 'Dogs Allowed' : 'Dogs Not Allowed',
                ...((values.catsAllowed || values.dogsAllowed) &&
                  lodashCompact([
                    values.dogsAllowed
                      ? !hasListingPetWeightLimit(values.petWeight) // NOTE: The petWeight question is only enable for dogs
                        ? 'Max weight - None'
                        : `Max weight of ${values.petWeight} lbs`
                      : null,
                    `Max of ${values.maximumPetsAllowed} ${
                      values.maximumPetsAllowed >= 2 ? 'pets' : 'pet'
                    }`,
                    typeof values.petFee === 'number' && `$${values.petFee} Fee`,
                    typeof values.petDeposit === 'number' && `$${values.petDeposit} Deposit`,
                    typeof values.monthlyPetRent === 'number' && `$${values.monthlyPetRent} Rent`,
                  ])),
              ]}
              onEditClick={() => onEditStep('pets')}
            />
            <SrLocalSeparator />
            <SrLocalInfoTile
              label="Want Property Management?"
              value={
                values.clientWantsPropertyManagement === true
                  ? 'Yes'
                  : values.clientWantsPropertyManagement === false
                  ? 'No'
                  : 'Not Sure'
              }
              onEditClick={() => onEditStep(isLandlord ? 'occupant' : 'address')}
            />
            <SrLocalInfoTile
              label="Extra Notes"
              value={values.partnerMarketingNotes || 'None'}
              onEditClick={() => onEditStep('notes')}
            />
          </>
        )}
      </SrCard>
    </>
  );
};

SrPropertySummary.propTypes = {
  formikBag: PropTypes.object.isRequired,
};

SrPropertySummary.displayName = 'SrPropertySummary';

export default SrPropertySummary;
